// Rene hjelpefunksjoner for åpningstider på bingohaller.
// Hall-objektet fra /halls og /halls/my har feltene
// open_weekday/close_weekday, open_saturday/close_saturday, open_sunday/close_sunday
// som "HH:mm"-strenger eller null. All beregning skjer i nettleserens lokale tid.

export const DAY_NAMES = [
  "søndag",
  "mandag",
  "tirsdag",
  "onsdag",
  "torsdag",
  "fredag",
  "lørdag",
];

// "10:00:00" | "10:00" | null -> "10:00" | null
export function normalizeTime(t) {
  if (t === null || t === undefined || t === "") return null;
  const m = String(t).match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  return m[1].padStart(2, "0") + ":" + m[2];
}

// "10:00" -> 600 (minutter siden midnatt), null -> null
export function toMinutes(t) {
  const n = normalizeTime(t);
  if (!n) return null;
  const [h, m] = n.split(":").map(Number);
  return h * 60 + m;
}

// Dag-aritmetikk uten DST-drift
export function addDays(d, n) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

// Åpningsvindu for ukedagen til `date`
export function getDayHours(hall, date) {
  if (!hall) return { open: null, close: null };
  const day = date.getDay();
  let open, close;
  if (day === 0) {
    open = hall.open_sunday;
    close = hall.close_sunday;
  } else if (day === 6) {
    open = hall.open_saturday;
    close = hall.close_saturday;
  } else {
    open = hall.open_weekday;
    close = hall.close_weekday;
  }
  return { open: normalizeTime(open), close: normalizeTime(close) };
}

export function hasAnyHours(hall) {
  if (!hall) return false;
  return [
    hall.open_weekday,
    hall.close_weekday,
    hall.open_saturday,
    hall.close_saturday,
    hall.open_sunday,
    hall.close_sunday,
  ].some((t) => normalizeTime(t) !== null);
}

// -> { hasHours, isOpen, text }
export function getOpenStatus(hall, now = new Date()) {
  if (!hasAnyHours(hall)) return { hasHours: false, isOpen: false, text: "" };

  const nowMin = now.getHours() * 60 + now.getMinutes();

  // A: fortsatt inne i gårsdagens vindu som krysset midnatt (f.eks. 18:00–01:00 kl. 00:30)
  const y = getDayHours(hall, addDays(now, -1));
  if (y.open && y.close) {
    const yo = toMinutes(y.open);
    const yc = toMinutes(y.close);
    if (yc <= yo && nowMin < yc) {
      return { hasHours: true, isOpen: true, text: `Åpen til ${y.close}` };
    }
  }

  // B: dagens vindu
  const t = getDayHours(hall, now);
  if (t.open && t.close) {
    const o = toMinutes(t.open);
    const c = toMinutes(t.close);
    const crossesMidnight = c <= o;
    if (nowMin >= o && (crossesMidnight || nowMin < c)) {
      return { hasHours: true, isOpen: true, text: `Åpen til ${t.close}` };
    }
    if (nowMin < o) {
      return { hasHours: true, isOpen: false, text: `Stengt – åpner ${t.open}` };
    }
  }

  // C: neste dag med åpningstid, 1–7 dager frem
  for (let i = 1; i <= 7; i++) {
    const d = addDays(now, i);
    const h = getDayHours(hall, d);
    if (h.open && h.close) {
      const label = i === 1 ? "i morgen" : DAY_NAMES[d.getDay()];
      return { hasHours: true, isOpen: false, text: `Stengt – åpner ${label} ${h.open}` };
    }
  }

  return { hasHours: true, isOpen: false, text: "Stengt" };
}

// -> "Man–fre 10:00–22:00 · Lør 10:00–20:00 · Søn stengt"
export function formatHoursSummary(hall) {
  if (!hall) return "";
  const part = (label, open, close) => {
    const o = normalizeTime(open);
    const c = normalizeTime(close);
    return o && c ? `${label} ${o}–${c}` : `${label} stengt`;
  };
  return [
    part("Man–fre", hall.open_weekday, hall.close_weekday),
    part("Lør", hall.open_saturday, hall.close_saturday),
    part("Søn", hall.open_sunday, hall.close_sunday),
  ].join(" · ");
}
